/**
 * Public /status page — pulls live health metrics from /api/status/public.
 * No auth required. Shows API + DB + notification integrations at a glance.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, CheckCircle2, XCircle, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Status() {
    const [state, setState] = useState({ loading: true, data: null, error: null });

    async function load() {
        try {
            const r = await fetch(`${API}/api/status/public`);
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const d = await r.json();
            setState({ loading: false, data: d, error: null });
        } catch (e) {
            setState({ loading: false, data: null, error: e.message });
        }
    }
    useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

    const overall = state.data?.overall || (state.error ? "down" : "checking");

    return (
        <div className="min-h-screen bg-stone-50">
            <div className="max-w-3xl mx-auto px-6 py-16">
                <Link to="/" className="text-xs font-outfit uppercase tracking-[0.2em] text-stone-500 hover:text-emerald-700">
                    ← Back to Infini HealthCare
                </Link>

                <div className="mt-6 flex items-center gap-3">
                    <Activity className="w-8 h-8 text-emerald-700" />
                    <h1 className="font-outfit font-semibold text-4xl text-stone-900 tracking-tight">
                        System Status
                    </h1>
                </div>

                <div className={`mt-8 rounded-2xl p-6 border-2 ${overall === "operational" ? "bg-emerald-50 border-emerald-200" : overall === "degraded" ? "bg-amber-50 border-amber-200" : "bg-stone-100 border-stone-200"}`} data-testid="status-overall">
                    <div className="flex items-center gap-3">
                        {overall === "operational" && <CheckCircle2 className="w-6 h-6 text-emerald-700" />}
                        {overall === "degraded" && <Clock className="w-6 h-6 text-amber-700" />}
                        {overall === "down" && <XCircle className="w-6 h-6 text-red-700" />}
                        {overall === "checking" && <Clock className="w-6 h-6 text-stone-500 animate-pulse" />}
                        <div>
                            <div className="font-outfit font-semibold text-lg text-stone-900">
                                {overall === "operational" && "All systems operational"}
                                {overall === "degraded" && "Partial degradation"}
                                {overall === "down" && "Service disruption"}
                                {overall === "checking" && "Checking…"}
                            </div>
                            <div className="text-xs text-stone-500 font-dmsans mt-0.5">
                                Refreshes every 30 seconds · Last check: {state.data?.checked_at || "—"}
                            </div>
                        </div>
                    </div>
                </div>

                {state.data?.components && (
                    <div className="mt-6 space-y-2" data-testid="status-components">
                        {state.data.components.map((c) => (
                            <div key={c.name} className="flex items-center justify-between bg-white border border-stone-200 rounded-xl p-4">
                                <div className="min-w-0">
                                    <div className="font-outfit font-semibold text-sm text-stone-900">{c.label}</div>
                                    <div className="text-xs text-stone-500 font-dmsans">{c.description}</div>
                                </div>
                                <StatusPill state={c.state} />
                            </div>
                        ))}
                    </div>
                )}

                {state.data?.metrics && (
                    <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-2">
                        <Metric label="Uptime (24h)" value={`${state.data.metrics.uptime_pct}%`} />
                        <Metric label="Response p50" value={`${state.data.metrics.p50_ms}ms`} />
                        <Metric label="Registered users" value={state.data.metrics.users.toLocaleString()} />
                        <Metric label="Blasts sent (7d)" value={state.data.metrics.blasts_7d.toLocaleString()} />
                    </div>
                )}

                <p className="mt-8 text-xs text-stone-500 font-dmsans">
                    For incident history, subscribe to updates at <a className="text-emerald-700 underline" href="mailto:status@careco24.com">status@careco24.com</a>.
                </p>
            </div>
        </div>
    );
}

function StatusPill({ state }) {
    const s = state || "unknown";
    const map = {
        operational: { bg: "bg-emerald-100 text-emerald-700", label: "Operational" },
        degraded: { bg: "bg-amber-100 text-amber-700", label: "Degraded" },
        outage: { bg: "bg-red-100 text-red-700", label: "Outage" },
        mocked: { bg: "bg-stone-100 text-stone-600", label: "Mocked" },
        unknown: { bg: "bg-stone-100 text-stone-500", label: "Unknown" },
    };
    const cfg = map[s] || map.unknown;
    return (
        <span data-testid={`status-pill-${s}`} className={`inline-flex items-center gap-1 px-2.5 h-6 rounded-full text-[11px] font-outfit font-medium ${cfg.bg}`}>
            {cfg.label}
        </span>
    );
}

function Metric({ label, value }) {
    return (
        <div className="bg-white border border-stone-200 rounded-xl p-3">
            <div className="text-[10px] uppercase font-outfit font-bold tracking-[0.15em] text-stone-500">{label}</div>
            <div className="font-outfit font-bold text-2xl text-stone-900 mt-1">{value}</div>
        </div>
    );
}
